import { useFulfilledDateTransformer } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/fulfilled-date-transformer';
import { useSalesPortalLastPurchaseInfoQuery } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/documents/sales-portal-last-purchase-info.generated';
import type { LastPurchaseInfoMap } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/fulfilled-date-transformer';

/**
 * Fetches the most recent purchase info for each variant pack in a
 * supplier–restaurant relationship and returns a lookup map keyed by
 * variant pack UUID.
 *
 * The query result is shaped into the map by `useFulfilledDateTransformer`,
 * which pre-formats the display string per row so cells don't redo i18n
 * work on every render.
 *
 * @param chatUUID - The chat UUID representing the supplier–restaurant relationship.
 * @param restaurantTimezone - IANA timezone identifier (e.g. "America/New_York") used to interpret and render dates.
 * @returns An object containing:
 *   - `lastPurchaseInfoMap` — a record keyed by variant pack UUID with raw dates, quantity, and a pre-formatted display string.
 *   - `loading` — whether the query is still in flight.
 */
export const useLastPurchaseInfoLoader = (
  chatUUID: string,
  restaurantTimezone: string,
): { lastPurchaseInfoMap: LastPurchaseInfoMap; loading: boolean } => {
  const { data, loading } = useSalesPortalLastPurchaseInfoQuery({
    fetchPolicy: 'cache-first',
    variables: { chatUUID },
  });

  const lastPurchaseInfoMap = useFulfilledDateTransformer(
    data?.getVariantPackLastPurchaseInfo,
    restaurantTimezone,
  );

  return { lastPurchaseInfoMap, loading };
};
